import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { ApiError, apiFetch, apiUploadFormData } from "@/lib/api";
import { formatNumber, type MrpPurchaseOrderLine } from "@/lib/mrp";
import { type PurchaseOrderFinancialDocLineInput } from "@/lib/masterData";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { CardTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApInvoiceDocumentPanel } from "@/pages/accounting/ApInvoiceDocumentPanel";
import { formatMoney } from "@/pages/accounting/suntronicInvoicesFormat";
import { deferredCommitDirtyClassName } from "./deferredCommitFieldShared";

export type PurchaseOrderFinancialDocKind = "gipi";

function parseQuantityInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number.parseFloat(trimmed.replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function parseUnitPriceInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number.parseFloat(trimmed.replace(/,/g, ""));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

function formatUnitPriceDefault(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return "";
  }
  return String(value);
}

function normalizePurchaseOrderUnitPrice(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }
  return value;
}

function unitPricesEqual(left: number | null, right: number | null): boolean {
  if (left == null && right == null) {
    return true;
  }
  if (left == null || right == null) {
    return false;
  }
  return Math.abs(left - right) < 1e-9;
}

function isUnitPriceDirtyFromGpo(
  unitPriceRaw: string,
  purchaseOrderUnitPrice: number | null | undefined,
): boolean {
  const baseline = normalizePurchaseOrderUnitPrice(purchaseOrderUnitPrice);
  const parsed = parseUnitPriceInput(unitPriceRaw);
  return !unitPricesEqual(parsed, baseline);
}

function purchaseOrderLineRemainingToInvoice(line: MrpPurchaseOrderLine): number {
  if (line.available_to_invoice != null && Number.isFinite(line.available_to_invoice)) {
    return Math.max(0, line.available_to_invoice);
  }
  return Math.max(0, line.quantity_ordered - line.quantity_received);
}

function purchaseOrderLineInvoicedQuantity(line: MrpPurchaseOrderLine): number {
  if (line.quantity_invoiced != null && Number.isFinite(line.quantity_invoiced)) {
    return line.quantity_invoiced;
  }
  return line.quantity_received;
}

function lineExtensionAmount(quantity: number | null, unitPrice: number | null): number | null {
  if (quantity == null || quantity <= 0 || unitPrice == null) {
    return null;
  }
  return quantity * unitPrice;
}

function todayIsoDateInput(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function financialDocKindLabel(): string {
  return "GIPI";
}

interface HubGipiCreateRequestBody {
  lines: PurchaseOrderFinancialDocLineInput[];
  vendor_invoice_number?: string;
  invoice_date?: string;
}

export interface PurchaseOrderFinancialDocDialogProps {
  purchaseOrderId: number;
  lines: MrpPurchaseOrderLine[];
  isHubOwned?: boolean;
  hubWritesAllowed: boolean;
  hubReadOnlyTitle: string;
  onClose: () => void;
  onCreated: (result: { mv_document_id?: number }) => void;
}

export function PurchaseOrderFinancialDocDialog({
  purchaseOrderId,
  lines,
  isHubOwned = false,
  hubWritesAllowed,
  hubReadOnlyTitle,
  onClose,
  onCreated,
}: PurchaseOrderFinancialDocDialogProps): JSX.Element {
  const [quantitiesByItemId, setQuantitiesByItemId] = useState<Record<number, string>>({});
  const isHubGipi = isHubOwned;
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState("");
  const [unitPricesByItemId, setUnitPricesByItemId] = useState<Record<number, string>>({});
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceFilePreviewUrl, setInvoiceFilePreviewUrl] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const lineSummaries = useMemo(
    () =>
      lines.map((line) => {
        const remaining = purchaseOrderLineRemainingToInvoice(line);
        return {
          line,
          remaining,
          invoiced: purchaseOrderLineInvoicedQuantity(line),
        };
      }),
    [lines],
  );

  const totalRemaining = useMemo(
    () => lineSummaries.reduce((sum, entry) => sum + entry.remaining, 0),
    [lineSummaries],
  );

  const createMutation = useMutation({
    mutationFn: async (request: HubGipiCreateRequestBody | PurchaseOrderFinancialDocLineInput[]) => {
      const body: HubGipiCreateRequestBody = Array.isArray(request) ? { lines: request } : request;
      if (isHubGipi && invoiceFile != null) {
        const formData = new FormData();
        formData.append("payload", JSON.stringify(body));
        formData.append("file", invoiceFile);
        return apiUploadFormData(
          `/api/mrp/financial-documents/gipi/from-purchase-order/${purchaseOrderId}`,
          formData,
        );
      }
      return apiFetch(`/api/mrp/financial-documents/gipi/from-purchase-order/${purchaseOrderId}`, {
        method: "POST",
        body,
      });
    },
    onSuccess: (result) => {
      if (invoiceFilePreviewUrl != null) {
        URL.revokeObjectURL(invoiceFilePreviewUrl);
      }
      onCreated(result as { mv_document_id?: number });
      onClose();
    },
  });

  const saveErrorMessage = useMemo(() => {
    if (localError) {
      return localError;
    }
    if (createMutation.error == null) {
      return null;
    }
    const error = createMutation.error;
    if (error instanceof ApiError) {
      if (error.body != null && typeof error.body === "object") {
        const body = error.body as Record<string, unknown>;
        const detail = typeof body.detail === "string" ? body.detail.trim() : "";
        if (detail !== "") {
          return detail;
        }
        const apiError = typeof body.error === "string" ? body.error.trim() : "";
        if (apiError !== "") {
          return apiError;
        }
      }
      return error.message;
    }
    return error instanceof Error ? error.message : String(error);
  }, [createMutation.error, localError]);

  const parsedDraft = useMemo(() => {
    return lineSummaries.map(({ line, remaining, invoiced }) => {
      const quantityRaw = quantitiesByItemId[line.id] ?? "";
      const quantityParsed = parseQuantityInput(quantityRaw);
      const quantityHasInvalid = quantityRaw.trim() !== "" && quantityParsed == null;
      const quantityExceeds = quantityParsed != null && quantityParsed > remaining + 1e-6;

      const defaultUnitPrice = formatUnitPriceDefault(line.unit_price);
      const unitPriceRaw = unitPricesByItemId[line.id] ?? defaultUnitPrice;
      const unitPriceParsed = parseUnitPriceInput(unitPriceRaw);
      const unitPriceHasInvalid = unitPriceRaw.trim() !== "" && unitPriceParsed == null;
      const unitPriceDirty = isUnitPriceDirtyFromGpo(unitPriceRaw, line.unit_price);

      return {
        line,
        remaining,
        invoiced,
        quantityRaw,
        quantityParsed,
        quantityHasInvalid,
        quantityExceeds,
        unitPriceRaw,
        unitPriceParsed,
        unitPriceHasInvalid,
        unitPriceDirty,
      };
    });
  }, [lineSummaries, quantitiesByItemId, unitPricesByItemId]);

  const totalEnteredQuantity = parsedDraft.reduce(
    (sum, entry) => sum + (entry.quantityParsed != null && entry.quantityParsed > 0 ? entry.quantityParsed : 0),
    0,
  );

  const gipiDocumentTotal = parsedDraft.reduce((sum, entry) => {
    if (entry.quantityParsed == null || entry.quantityParsed <= 0) {
      return sum;
    }
    const unitPrice =
      entry.unitPriceParsed ?? normalizePurchaseOrderUnitPrice(entry.line.unit_price);
    if (unitPrice == null) {
      return sum;
    }
    return sum + entry.quantityParsed * unitPrice;
  }, 0);

  const hasInvalidLine = parsedDraft.some(
    (entry) => entry.quantityHasInvalid || entry.quantityExceeds || entry.unitPriceHasInvalid,
  );
  const hasPositiveLine = parsedDraft.some((entry) => entry.quantityParsed != null && entry.quantityParsed > 0);

  const dialogHelpText = useMemo(() => {
    if (isHubGipi) {
      return (
        "Creates a parentless Megaventory GIPI for this hub purchase order (direct GIPI path). " +
        "Does not create a vendor invoice or inbound receipt; that is the separate two-half review flow. " +
        "Megaventory gets supplier, location, payment terms from the purchase order, line quantities/prices, " +
        "and a note linking back to this purchase order. The vendor invoice number is required. It is saved on the GIPI " +
        "and sent to Megaventory as the document reference. The uploaded invoice file stays in the hub. Invoice date is always today. Quantities start at 0; use All " +
        "for the remaining quantity on a line. Unit prices start from the purchase order. Megaventory assigns the " +
        "GIPI document number after create."
      );
    }
    if (isHubOwned) {
      return (
        `Quantities start at 0; use All for the remaining quantity on a line. Unit prices start from the purchase order. ` +
        `Edit either before creating this GIPI; ordered quantity is the maximum per line.`
      );
    }
    return (
      `Quantities start at 0; use All for the remaining quantity on a line. Unit prices start from the purchase order. ` +
      `Edit either before creating this GIPI; ordered quantity is the maximum per line.`
    );
  }, [isHubGipi, isHubOwned]);

  const vendorInvoiceNumberMissing = isHubGipi && vendorInvoiceNumber.trim() === "";

  const saveDisabledHint =
    !hubWritesAllowed || totalRemaining <= 0
      ? ""
      : createMutation.isPending
        ? ""
        : vendorInvoiceNumberMissing
          ? "Enter the vendor invoice number before creating this GIPI."
          : hasInvalidLine
            ? "Enter valid quantities and unit prices for each line you fill in."
            : !hasPositiveLine
              ? "Enter a positive quantity on at least one line before creating the document."
              : "";

  const saveDisabled =
    !hubWritesAllowed ||
    totalRemaining <= 0 ||
    createMutation.isPending ||
    vendorInvoiceNumberMissing ||
    hasInvalidLine ||
    !hasPositiveLine;

  const dialogTitle = `Create ${financialDocKindLabel()}${isHubOwned ? "" : " in Megaventory"}`;
  const hubGipiTwoColumn = isHubGipi;

  const linesTable = totalRemaining <= 0 ? (
    <p className="text-sm text-amber-800">
      Every line on this purchase order is already fully invoiced on a GIPI or vendor-invoice draft. Open an existing
      GIPI instead of creating another one.
    </p>
  ) : (
    <>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="px-3 py-2 font-medium">SKU</th>
              <th className="px-3 py-2 font-medium text-right">Ordered</th>
              <th className="px-3 py-2 font-medium text-right">Already invoiced</th>
              <th className="px-3 py-2 font-medium text-right">Remaining</th>
              <th className="px-3 py-2 font-medium text-right">Quantity on this document</th>
              <th className="px-3 py-2 font-medium text-right">Unit price</th>
              <th className="px-3 py-2 font-medium text-right">Line total</th>
            </tr>
          </thead>
          <tbody>
            {parsedDraft.map(
              ({
                line,
                remaining,
                invoiced,
                quantityRaw,
                quantityParsed,
                quantityHasInvalid,
                quantityExceeds,
                unitPriceRaw,
                unitPriceParsed,
                unitPriceHasInvalid,
                unitPriceDirty,
              }) => {
                const effectiveUnitPrice =
                  unitPriceParsed ?? normalizePurchaseOrderUnitPrice(line.unit_price);
                const lineTotal = lineExtensionAmount(quantityParsed, effectiveUnitPrice);
                return (
                  <tr key={line.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-slate-900">{line.cpn}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(line.quantity_ordered)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(invoiced)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(remaining)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-2">
                        <Input
                          className={cn(
                            "w-28 text-right tabular-nums",
                            (quantityHasInvalid || quantityExceeds) && "border-rose-500",
                          )}
                          inputMode="decimal"
                          disabled={!hubWritesAllowed || remaining <= 0 || createMutation.isPending}
                          value={quantityRaw}
                          placeholder={remaining > 0 ? "0" : "—"}
                          onChange={(event) => {
                            setLocalError(null);
                            setQuantitiesByItemId((current) => ({
                              ...current,
                              [line.id]: event.target.value,
                            }));
                          }}
                        />
                        {remaining > 0 ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={!hubWritesAllowed || createMutation.isPending}
                            onClick={() => {
                              setLocalError(null);
                              setQuantitiesByItemId((current) => ({
                                ...current,
                                [line.id]: String(remaining),
                              }));
                            }}
                          >
                            All
                          </Button>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        className={cn(
                          "w-28 text-right tabular-nums",
                          unitPriceHasInvalid && "border-rose-500",
                          deferredCommitDirtyClassName(
                            unitPriceDirty,
                            !hubWritesAllowed || remaining <= 0 || createMutation.isPending,
                          ),
                        )}
                        inputMode="decimal"
                        disabled={!hubWritesAllowed || remaining <= 0 || createMutation.isPending}
                        value={unitPriceRaw}
                        placeholder="0"
                        onChange={(event) => {
                          setLocalError(null);
                          setUnitPricesByItemId((current) => ({
                            ...current,
                            [line.id]: event.target.value,
                          }));
                        }}
                      />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                      {lineTotal != null ? formatMoney(lineTotal) : "—"}
                    </td>
                  </tr>
                );
              },
            )}
          </tbody>
        </table>
      </div>
      {totalEnteredQuantity > 0 ? (
        <div className="flex flex-wrap items-baseline justify-end gap-x-4 gap-y-1 text-sm tabular-nums text-slate-700">
          <span>
            Total quantity: <span className="font-mono font-medium">{formatNumber(totalEnteredQuantity)}</span>
          </span>
          <span className="font-semibold text-slate-900">
            GIPI total: <span className="font-mono">{formatMoney(gipiDocumentTotal)}</span>
          </span>
        </div>
      ) : null}
    </>
  );

  const documentPanel = isHubGipi ? (
    <ApInvoiceDocumentPanel
      layout="fill"
      file={invoiceFile}
      previewUrl={invoiceFilePreviewUrl}
      filename={invoiceFile?.name ?? null}
      disabled={!hubWritesAllowed || createMutation.isPending}
      isUploading={createMutation.isPending}
      onFileChange={(next) => {
        setLocalError(null);
        if (invoiceFilePreviewUrl != null) {
          URL.revokeObjectURL(invoiceFilePreviewUrl);
        }
        setInvoiceFile(next);
        setInvoiceFilePreviewUrl(next != null ? URL.createObjectURL(next) : null);
      }}
      onRemove={() => {
        setLocalError(null);
        if (invoiceFilePreviewUrl != null) {
          URL.revokeObjectURL(invoiceFilePreviewUrl);
        }
        setInvoiceFile(null);
        setInvoiceFilePreviewUrl(null);
      }}
    />
  ) : null;

  const actionFooter = (
    <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
      <Button size="sm" variant="secondary" type="button" disabled={createMutation.isPending} onClick={onClose}>
        Cancel
      </Button>
      <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={hubReadOnlyTitle} layout="inline">
        <ReadOnlyHoverLayer
          active={hubWritesAllowed && saveDisabled && saveDisabledHint !== ""}
          hoverHint={saveDisabledHint}
          layout="inline"
        >
          <Button
            size="sm"
            variant="primary"
            type="button"
            isLoading={createMutation.isPending}
            disabled={saveDisabled}
            onClick={() => {
              if (!hubWritesAllowed || saveDisabled) {
                return;
              }
              const payload: PurchaseOrderFinancialDocLineInput[] = [];
              for (const entry of parsedDraft) {
                if (entry.quantityParsed == null || entry.quantityParsed <= 0) {
                  continue;
                }
                const unitPrice =
                  entry.unitPriceParsed ?? (entry.line.unit_price != null ? entry.line.unit_price : null);
                payload.push({
                  purchase_order_item_id: entry.line.id,
                  quantity: entry.quantityParsed,
                  unit_price: unitPrice,
                });
              }
              if (payload.length === 0) {
                setLocalError("Enter a positive quantity on at least one line.");
                return;
              }
              setLocalError(null);
              if (isHubGipi) {
                const trimmedVendorNumber = vendorInvoiceNumber.trim();
                createMutation.mutate({
                  lines: payload,
                  invoice_date: todayIsoDateInput(),
                  ...(trimmedVendorNumber !== "" ? { vendor_invoice_number: trimmedVendorNumber } : {}),
                });
                return;
              }
              createMutation.mutate(payload);
            }}
          >
            Create {financialDocKindLabel()}
          </Button>
        </ReadOnlyHoverLayer>
      </ReadOnlyHoverLayer>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={dialogTitle}
    >
      <Card
        className={cn(
          "mx-auto flex w-full flex-col overflow-hidden shadow-xl",
          hubGipiTwoColumn ? "h-full max-h-none max-w-7xl" : "max-h-[90vh] max-w-4xl self-center overflow-y-auto",
        )}
      >
        <CardHeader className="shrink-0">
          <CardTitleWithHelp hoverHint={dialogHelpText} titleClassName="text-base font-semibold text-slate-900">
            Create {financialDocKindLabel()}
            {isHubOwned ? "" : " in Megaventory"}
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody
          className={cn(
            "flex min-h-0 flex-1 flex-col gap-3",
            hubGipiTwoColumn ? "overflow-hidden" : "overflow-y-auto",
          )}
        >
          {hubGipiTwoColumn ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden lg:grid lg:grid-cols-2 lg:gap-4">
              <div className="flex min-h-0 min-w-0 flex-col gap-3 overflow-y-auto pr-0 lg:pr-1">
                <label className="block shrink-0 space-y-1 text-sm">
                  <span className="font-medium text-slate-700">Vendor invoice number</span>
                  <Input
                    value={vendorInvoiceNumber}
                    disabled={!hubWritesAllowed || createMutation.isPending}
                    placeholder="As printed on the vendor invoice"
                    onChange={(event) => {
                      setLocalError(null);
                      setVendorInvoiceNumber(event.target.value);
                    }}
                  />
                </label>
                {linesTable}
                {saveErrorMessage ? <p className="text-sm text-rose-700">{saveErrorMessage}</p> : null}
              </div>
              <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">{documentPanel}</div>
            </div>
          ) : (
            <div className="space-y-3">
              {linesTable}
              {saveErrorMessage ? <p className="text-sm text-rose-700">{saveErrorMessage}</p> : null}
            </div>
          )}
          {actionFooter}
        </CardBody>
      </Card>
    </div>
  );
}
